/* Copyright (C) 2023 Advenio Tecnosys Pvt. Ltd.

*        All Rights Reserved

*

 * NOTICE: 

 * All information contained herein is, and remains

* the property of Advenio Tecnosys and its suppliers,

* if any.  The intellectual and technical concepts contained

* herein are proprietary to Advenio Tecnosys

* and its suppliers and may be covered by Indian, U.S. and Foreign Patents,

* patents in process, and are protected by trade secret or copyright law.

* Appropriation, redistribution, dissemination of this information or reproduction

* of this material is strictly forbidden unless prior written permission is obtained

* from Advenio Tecnosys Pvt. Ltd.

*

* Author: < Mylavarapu Sarath Sai, Amal Sabu, Ujjwal Singh >

*/
import { getBulkRowTuples } from "../realtime/bulkUploadProjection";

const defaultState = {
  viewImagesList: [],
  selectedViewImagesList: [],
  saveViewImagesRes: "",
  reportCountAlert: null,
  totalBulkImagesCount: 0,
  allImagesReportGenInQueue: false,
  requestGeneration: 0,
  realtimeDescriptor: null,
  realtimeRows: {},
};

const rebaseSelectedRows = (selectedRows = [], rows = []) => {
  const rowsByGuid = new Map(rows.map((row) => [row?.guid, row]));
  return selectedRows
    .map((row) => rowsByGuid.get(row?.guid))
    .filter(Boolean);
};

const mergeRealtimeRows = (rows, realtimeRows) =>
  rows.map((row) => (realtimeRows || {})[row?.guid] || row);

export default (state = defaultState, action) => {
  switch (action.type) {
    //   store
    case "CREATE_VIEW_IMAGE_LIST": {
      return {
        ...state,
        viewImagesList: action.payload,
        loading: false,
        errors: {},
      };
    }
    case "SELECTED_VIEW_IMAGE_LIST": {
      return {
        ...state,
        selectedViewImagesList: action.payload,
        loading: false,
        errors: {},
      };
    }
    case "ADD_VIEW_IMAGE_PENDING": {
      return {
        ...state,
        loading: true,
        errors: {},
      };
    }
    case "ADD_VIEW_IMAGE_FULFILLED": {
      return {
        ...state,
        saveBulkImgRes: action.payload,
        viewImagesList: action.payload.data,
        loading: false,
        errors: {},
      };
    }
    case "ADD_VIEW_IMAGE_REJECTED": {
      return {
        ...state,
        loading: false,
        errors: action.payload.errors,
      };
    }
    case "GET_VIEW_IMAGE_PENDING": {
      return {
        ...state,
        loading: true,
        errors: {},
      };
    }
    case "GET_VIEW_IMAGE": {
      const viewImagesList = mergeRealtimeRows(
        action.payload.viewImagesList,
        state.realtimeRows
      );
      return {
        ...state,
        viewImagesList,
        totalBulkImagesCount: action.payload.count,
        loading: false,
        errors: {},
      };
    }
    case "GET_VIEW_IMAGE_REJECTED": {
      return {
        ...state,
        loading: false,
        errors: action.payload.errors,
      };
    }
    case "UPDATE_VIEW_IMAGE_LIST_BY_ID": {
      const viewImagesList = state.viewImagesList.map((element) =>
        element.guid === action.payload.guid ? action.payload : element
      );
      return {
        ...state,
        viewImagesList,
        selectedViewImagesList: rebaseSelectedRows(
          state.selectedViewImagesList,
          viewImagesList
        ),
        loading: false,
        errors: {},
        realtimeRows: action.meta?.realtime
          ? { ...state.realtimeRows, [action.payload.guid]: action.payload }
          : state.realtimeRows,
      };
    }
    case "TRIGGER_VIEW_IMAGES_REPORT_COUNT_ALERT": {
      return {
        ...state,
        reportCountAlert: action.payload,
      };
    }
    case "SWAP_VIEW_IMAGES": {
      const viewImagesList = mergeRealtimeRows(
        action.payload.viewImagesList,
        state.realtimeRows
      );
      return {
        ...state,
        viewImagesList,
        selectedViewImagesList: rebaseSelectedRows(
          state.selectedViewImagesList,
          viewImagesList
        ),
        totalBulkImagesCount: action.payload.count,
      };
    }
    case "BEGIN_VIEW_IMAGES_REQUEST": {
      return {
        ...state,
        requestGeneration: action.payload.generation,
        realtimeDescriptor: null,
        realtimeRows: {},
      };
    }
    case "COMMIT_VIEW_IMAGES_REQUEST": {
      if (action.payload.generation !== state.requestGeneration) return state;
      const viewImagesList = mergeRealtimeRows(
        action.payload.viewImagesList,
        state.realtimeRows
      );
      return {
        ...state,
        viewImagesList,
        selectedViewImagesList: rebaseSelectedRows(
          state.selectedViewImagesList,
          viewImagesList
        ),
        totalBulkImagesCount: action.payload.count,
        realtimeDescriptor: {
          ...action.payload.descriptor,
          generation: action.payload.generation,
        },
      };
    }
    case "RETIRE_VIEW_IMAGES_REQUEST": {
      return {
        ...state,
        requestGeneration: action.payload.generation,
        realtimeDescriptor: null,
        realtimeRows: {},
      };
    }
    case "UPDATE_IMAGES_SELECTED": {
      return {
        ...state,
        selectedViewImagesList: action.payload.viewImagesList,
        viewImagesList: action.payload.viewImagesList,
        reportGenerationType: action.payload.downloadType,
        allImagesReportGenInQueue: true,
      };
    }
    case "DELETE_IMAGES_FROM_IDB_AND_STORE_PENDING": {
      return {
        ...state,
        loading: true,
      };
    }
    case "DELETE_IMAGES_FROM_IDB_AND_STORE": {
      const deletedGuids = new Set(action.payload.guidList || []);
      const viewImagesList = state.viewImagesList.filter(
        (row) => !deletedGuids.has(row?.guid)
      );
      const activeTuples = viewImagesList
        .filter((row) => Array.isArray(row?.status) && row.status.length > 0)
        .flatMap(getBulkRowTuples)
        .sort();
      return {
        ...state,
        selectedViewImagesList: rebaseSelectedRows(
          state.selectedViewImagesList,
          viewImagesList
        ),
        viewImagesList,
        totalBulkImagesCount: Math.max(
          0,
          state.totalBulkImagesCount - action.payload.noOfImagesToDelete
        ),
        realtimeDescriptor: state.realtimeDescriptor
          ? { ...state.realtimeDescriptor, activeTuples }
          : null,
        loading: false,
      };
    }

    default:
      return state;
  }
};
